import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Input, Empty, Tag, Space } from '@douyinfe/semi-ui'
import { IconSearch } from '@douyinfe/semi-icons'
import { useNavigate } from 'react-router-dom'
import Fuse, { IFuseOptions } from 'fuse.js'
import { useLocale } from '@src/locales'
import { create } from 'zustand'
import useMenuStore from '@src/store/common/menu'
import { MenuItem } from '@src/api/common/menu'
import { getLocalStorage, setLocalStorage } from '@src/utils/storage'

/**
 * 全局搜索（方案 3 混合 - 现阶段只做菜单搜索）
 * - 数据源：useMenuStore.menuList（已经在内存中，不需要额外请求）
 * - 业务搜索：等后端 /sys/search 接口就绪后接入，目前不展示
 *
 * 唤起方式：
 *   1. 顶栏点击搜索框
 *   2. 全局快捷键 Cmd+K / Ctrl+K
 *
 * UX：
 *   - ↑/↓ 键导航，Enter 跳转，Esc 关闭
 *   - 命中关键字高亮
 *   - 最近 5 次搜索 localStorage 持久化
 *   - 空输入时显示「最近搜索」
 */

const RECENT_KEY = 'global_search_recent'
const RECENT_MAX = 5

interface GlobalSearchState {
	open: boolean
	setOpen: (open: boolean) => void
	toggle: () => void
}

/** 全局状态：供 header 触发开 / 闭 */
export const useGlobalSearchStore = create<GlobalSearchState>((set, get) => ({
	open: false,
	setOpen: (open) => set({ open }),
	toggle: () => set({ open: !get().open })
}))

/** 拍平 + 翻译后的菜单项：fuse 索引数据源 */
interface IndexedMenuItem {
	itemKey: string
	/** 翻译后的展示名（建索引时已根据当前 locale 解析） */
	title: string
	/** 原始 i18n key（也作为索引字段，权重低） */
	textKey: string
	path?: string
	/** 父级面包屑，翻译后用 ' / ' 拼接（建索引时算好） */
	parentTitles: string
	/** 父级面包屑用 \u0000 拼接的隐藏字段，搜索父链时用 */
	parentTitlesKey: string
}

/**
 * 把菜单树拍平、翻译、转成 fuse 友好的数组
 *  - hide=true 的项跳过
 *  - 父链用换行符拼接（fuse 会忽略位置）
 *  - 用 i18nMsg 在建索引时翻译：locale 变化时整个数组重建
 */
const buildIndex = (menus: MenuItem[], i18nMsg: (descriptor: { id: string }) => string): IndexedMenuItem[] => {
	const out: IndexedMenuItem[] = []
	const walk = (items: MenuItem[], parentTitles: string[]) => {
		for (const m of items) {
			if (m.hide) continue
			const title = i18nMsg({ id: m.text }) || m.text
			const nextParents = [...parentTitles, title]
			if (m.path) {
				out.push({
					itemKey: m.itemKey,
					title,
					textKey: m.text,
					path: m.path,
					parentTitles: parentTitles.join(' / '),
					parentTitlesKey: parentTitles.join('\u0000')
				})
			}
			if (m.items) walk(m.items, nextParents)
		}
	}
	walk(menus, [])
	return out
}

/** fuse.js 索引配置：title 权重高，textKey 兜底；threshold 0.4 比较宽松 */
const FUSE_OPTIONS: IFuseOptions<IndexedMenuItem> = {
	keys: [
		{ name: 'title', weight: 0.7 },
		{ name: 'textKey', weight: 0.2 },
		{ name: 'parentTitlesKey', weight: 0.1 }
	],
	threshold: 0.4,
	ignoreLocation: true,
	minMatchCharLength: 1,
	includeScore: false
}

const getRecent = (): string[] => {
	try {
		const raw = getLocalStorage(RECENT_KEY)
		if (typeof raw !== 'string') return []
		const arr = JSON.parse(raw)
		return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []
	} catch {
		return []
	}
}

const pushRecent = (q: string) => {
	if (!q.trim()) return
	const list = getRecent().filter((x) => x !== q)
	list.unshift(q.trim())
	setLocalStorage(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)))
}

/** 高亮命中的关键字（大小写不敏感，只标第一个匹配位置） */
const highlight = (text: string, query: string): ReactNode => {
	if (!query.trim() || !text) return text
	const q = query.toLowerCase()
	const t = text.toLowerCase()
	const idx = t.indexOf(q)
	if (idx < 0) return text
	return (
		<>
			{text.slice(0, idx)}
			<mark
				style={{
					background: 'var(--semi-color-warning-light-default)',
					color: 'inherit',
					padding: 0,
					borderRadius: 2
				}}
			>
				{text.slice(idx, idx + q.length)}
			</mark>
			{text.slice(idx + q.length)}
		</>
	)
}

const GlobalSearch: FC = () => {
	const { open, setOpen, toggle } = useGlobalSearchStore()
	const [query, setQuery] = useState('')
	const [selectedIdx, setSelectedIdx] = useState(0)
	const [recents, setRecents] = useState<string[]>([])
	const inputRef = useRef<any>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const navigate = useNavigate()
	const { formatMessage: i18nMsg } = useLocale()
	const { menuList, fetchMenuList, loaded } = useMenuStore()

	/**
	 * 索引数据源：
	 *  - 拍平菜单树，跳过 hide 项
	 *  - 提前把 i18n key 翻译成当前 locale 的 title（locale 切换时整个数组重建）
	 *  - 父链面包屑也提前 join，避免渲染时再调 i18nMsg
	 */
	const indexedItems = useMemo(() => buildIndex(menuList, i18nMsg), [menuList, i18nMsg])

	/** fuse 实例：indexedItems 变化时重建（菜单 / 语言切换） */
	const fuse = useMemo(() => new Fuse(indexedItems, FUSE_OPTIONS), [indexedItems])

	/** 搜索结果：取 top 20，fuse 已按相关度排序 */
	const results = useMemo(() => {
		const q = query.trim()
		if (!q) return []
		return fuse
			.search(q)
			.slice(0, 20)
			.map((r) => r.item)
	}, [query, fuse])

	// 打开时：兜底拉一次菜单、读最近搜索、focus input
	useEffect(() => {
		if (!open) return
		if (!loaded) {
			fetchMenuList().catch(() => undefined)
		}
		setRecents(getRecent())
		setSelectedIdx(0)
		const t = window.setTimeout(() => inputRef.current?.focus?.(), 50)
		return () => window.clearTimeout(t)
	}, [open, loaded, fetchMenuList])

	// 结果变化时重置选中
	useEffect(() => {
		setSelectedIdx(0)
	}, [query])

	// 选中项滚到可视区
	useEffect(() => {
		if (!listRef.current) return
		const el = listRef.current.querySelector(`[data-idx="${selectedIdx}"]`)
		if (el) {
			el.scrollIntoView({ block: 'nearest' })
		}
	}, [selectedIdx])

	// 全局快捷键 Cmd/Ctrl + K 唤起 / 关闭
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			const isK = e.key === 'k' || e.key === 'K'
			if ((e.metaKey || e.ctrlKey) && isK) {
				e.preventDefault()
				toggle()
			}
		}
		window.addEventListener('keydown', handler)
		return () => window.removeEventListener('keydown', handler)
	}, [toggle])

	const handleSelect = (item: IndexedMenuItem) => {
		if (!item.path) return
		pushRecent(query)
		navigate(item.path)
		setOpen(false)
	}

	const handleInputKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setSelectedIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)))
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			setSelectedIdx((i) => Math.max(i - 1, 0))
		} else if (e.key === 'Enter') {
			e.preventDefault()
			const item = results[selectedIdx]
			if (item) handleSelect(item)
		} else if (e.key === 'Escape') {
			setOpen(false)
		}
	}

	const renderResultItem = (item: IndexedMenuItem, idx: number) => {
		const isSelected = idx === selectedIdx
		return (
			<div
				key={item.itemKey}
				data-idx={idx}
				onClick={() => handleSelect(item)}
				onMouseEnter={() => setSelectedIdx(idx)}
				style={{
					padding: '8px 12px',
					cursor: 'pointer',
					background: isSelected ? 'var(--semi-color-fill-0)' : 'transparent',
					borderRadius: 4,
					display: 'flex',
					flexDirection: 'column',
					gap: 2
				}}
			>
				<div style={{ fontSize: 14, fontWeight: 500, color: 'var(--semi-color-text-0)' }}>
					{highlight(item.title, query)}
				</div>
				{item.parentTitles && (
					<div style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>{item.parentTitles}</div>
				)}
			</div>
		)
	}

	return (
		<Modal
			visible={open}
			onCancel={() => setOpen(false)}
			footer={null}
			width={560}
			closable={false}
			style={{ top: 80 }}
			maskStyle={{ background: 'rgba(0,0,0,0.45)' }}
			bodyStyle={{ padding: 12 }}
		>
			<Input
				ref={inputRef}
				value={query}
				onChange={(v) => setQuery(v)}
				onKeyDown={handleInputKeyDown}
				placeholder={i18nMsg({ id: 'app.common.search.placeholder' })}
				prefix={<IconSearch />}
				size="large"
				style={{ border: 'none', boxShadow: 'none' }}
			/>
			<div
				ref={listRef}
				style={{
					maxHeight: 360,
					minHeight: 80,
					overflowY: 'auto',
					marginTop: 8,
					padding: '4px'
				}}
			>
				{!query.trim() ? (
					recents.length > 0 ? (
						<div>
							<div
								style={{
									fontSize: 12,
									color: 'var(--semi-color-text-2)',
									padding: '4px 8px 8px'
								}}
							>
								{i18nMsg({ id: 'app.common.search.recent' })}
							</div>
							<Space wrap style={{ padding: '0 8px' }}>
								{recents.map((r) => (
									<Tag key={r} onClick={() => setQuery(r)} style={{ cursor: 'pointer', userSelect: 'none' }}>
										{r}
									</Tag>
								))}
							</Space>
						</div>
					) : (
						<Empty description={i18nMsg({ id: 'app.common.search.empty' })} />
					)
				) : results.length === 0 ? (
					<Empty description={i18nMsg({ id: 'app.common.search.noResult' })} />
				) : (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{results.map(renderResultItem)}</div>
				)}
			</div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					padding: '8px 4px 0',
					fontSize: 12,
					color: 'var(--semi-color-text-2)',
					borderTop: '1px solid var(--semi-color-border)',
					marginTop: 4
				}}
			>
				<span>{i18nMsg({ id: 'app.common.search.hint' })}</span>
				<span>{i18nMsg({ id: 'app.common.search.hotkey' })}</span>
			</div>
		</Modal>
	)
}

export default GlobalSearch
